// ─── src/admin/pages/ExperiencePage.jsx ──────────────────────────────────────

import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber,
  Space, Tag, Popconfirm, message, Typography, Grid, Alert,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { experiencesService } from '../firebase/services';
import { useThemeColors } from '../hooks/Usethemecolors';
import ColorSelect from '../components/ColorSelect';

const { Title } = Typography;
const { useBreakpoint } = Grid;

export default function ExperiencePage() {
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form]      = Form.useForm();
  const screens     = useBreakpoint();
  const isMobile    = !screens.md;

  // ── Controlled color state — kept outside form ────────────────────────────
  // Mirrors CertificatesPage: Ant Design Form does not relay value/onChange
  // reliably to custom components using complex dropdown rendering.
  // We manage color separately and merge it into the payload at save time.
  const [selectedColor, setSelectedColor] = useState('#c8f04a');

  // ── Live theme colors from Firestore ───────────────────────────────────────
  const { themeColors, loading: themeLoading, error: themeError } = useThemeColors();

  const load = async () => {
    setLoading(true);
    try { setData(await experiencesService.getAll()); }
    catch { message.error('Failed to load experiences'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // ── Open modal ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    const defaultColor = themeColors.find(c => c.varName === '--acid')?.hex || '#c8f04a';
    setSelectedColor(defaultColor);
    form.setFieldsValue({ order: data.length + 1 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setSelectedColor(record.color || '#c8f04a');
    form.setFieldsValue({
      year:  record.year,
      role:  record.role,
      co:    record.co,
      desc:  record.desc,
      order: record.order,
    });
    setModalOpen(true);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      const payload = { ...vals, color: selectedColor }; // ← color merged here
      if (editing) {
        await experiencesService.update(editing.id, payload);
        message.success('Experience updated');
      } else {
        await experiencesService.add(payload);
        message.success('Experience added');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Save failed');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await experiencesService.remove(id);
      message.success('Deleted');
      load();
    } catch { message.error('Delete failed'); }
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Order',
      dataIndex: 'order',
      width: 60,
      responsive: ['md'],
    },
    {
      title: 'Period',
      dataIndex: 'year',
      width: 110,
      responsive: ['sm'],
      render: (y, r) => (
        <Tag color={r.color} style={{ color: '#000', whiteSpace: 'nowrap' }}>{y}</Tag>
      ),
    },
    {
      title: 'Role / Company',
      dataIndex: 'role',
      render: (t, r) => (
        <Space direction="vertical" size={2}>
          <strong>{t}</strong>
          <span style={{ color: '#888', fontSize: 12 }}>{r.co}</span>
          {isMobile && r.year && (
            <Tag color={r.color} style={{ color: '#000', fontSize: 11, marginTop: 2 }}>
              {r.year}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'co',
      responsive: ['lg'],
    },
    {
      title: 'Description',
      dataIndex: 'desc',
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: 'Actions',
      width: 80,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8,
      }}>
        <Title level={4} style={{ margin: 0 }}>Experience</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          Add Experience
        </Button>
      </div>

      {themeError && (
        <Alert message={themeError} type="warning" showIcon closable style={{ marginBottom: 12 }} />
      )}

      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={false}
        size={isMobile ? 'small' : 'middle'}
        scroll={{ x: 'max-content' }}
      />

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      <Modal
        title={editing ? 'Edit Experience' : 'Add Experience'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Save"
        width={isMobile ? '100%' : 520}
        style={isMobile ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : {}}
        styles={isMobile ? { body: { padding: '12px 16px' } } : {}}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="year" label="Period" rules={[{ required: true }]}>
            <Input placeholder="2025–Now" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Input placeholder="Frontend Developer" />
          </Form.Item>
          <Form.Item name="co" label="Company" rules={[{ required: true }]}>
            <Input placeholder="Company Name" />
          </Form.Item>
          <Form.Item name="desc" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>

          {/* ── Accent Color — controlled outside Form, same as CertificatesPage ── */}
          <Form.Item label="Accent Color" required>
            {/* Live swatch preview of currently selected color */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 4,
                background: selectedColor,
                border: '1px solid #d9d9d9',
                flexShrink: 0,
              }} />
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 12, color: '#555' }}>
                {selectedColor.toUpperCase()}
              </span>
            </div>
            <ColorSelect
              value={selectedColor}
              onChange={setSelectedColor}
              themeColors={themeColors}
              loading={themeLoading}
              storageKey="admin_custom_colors_experience"
            />
          </Form.Item>

          <Form.Item name="order" label="Order">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}